import {
  RELATIONSHIP_RISK_MODEL_2_LEVEL,
  RELATIONSHIP_RISK_MODEL_3_LEVEL,
  normalizeRelationshipRiskLevel as normalizeFinalRelationshipRiskLevel,
  normalizeRelationshipRiskModel,
  relationshipRiskLevelsForModel,
} from "./relationshipRiskRules";

export const SUPPORTING_DOCUMENT_POLICY_COLLECTION = "supportingDocumentRisk";

export const UBO_DOCUMENT_SCOPE_THRESHOLD_ONLY = "threshold_only";
export const UBO_DOCUMENT_SCOPE_THRESHOLD_AND_DIRECT = "threshold_and_direct";
export const UBO_DOCUMENT_SCOPE_ALL_UBOS = "all_ubos";
export const DEFAULT_UBO_DOCUMENT_SCOPE = UBO_DOCUMENT_SCOPE_THRESHOLD_ONLY;

export function normalizeUboDocumentScope(value) {
  const raw = String(value || "").trim();
  if (
    raw === UBO_DOCUMENT_SCOPE_THRESHOLD_ONLY ||
    raw === UBO_DOCUMENT_SCOPE_THRESHOLD_AND_DIRECT ||
    raw === UBO_DOCUMENT_SCOPE_ALL_UBOS
  ) {
    return raw;
  }
  return DEFAULT_UBO_DOCUMENT_SCOPE;
}

export const SUPPORTING_DOC_RISKS = [
  { key: "standard", label: "Standard risk" },
  { key: "medium", label: "Medium risk" },
  { key: "high", label: "High risk" },
];

export function supportingDocRisksForModel(model) {
  return relationshipRiskLevelsForModel(model);
}

export const SOW_DOCUMENT_SOURCES = [
  {
    key: "employment",
    label: "Employment",
    docs: [
      { key: "payslips", label: "Payslips" },
      { key: "employment_contract", label: "Employment contract" },
      { key: "tax_return", label: "Tax return" },
      { key: "bank_statements", label: "Bank statement(s)", optional: true },
    ],
  },
  {
    key: "self_employed",
    label: "Self-employed",
    docs: [
      { key: "contracts", label: "Copies of contracts" },
      { key: "tax_return", label: "Tax return" },
      { key: "audited_fs", label: "Audited financial statement(s)" },
      { key: "bank_statements", label: "Bank statement(s)", optional: true },
    ],
  },
  {
    key: "business_owner",
    label: "Business owner",
    docs: [
      { key: "shareholder_register", label: "Shareholder register" },
      { key: "audited_fs", label: "Audited financial statement(s)" },
      { key: "corporate_doc", label: "Corporate document" },
      { key: "bank_statements", label: "Bank statement(s)", optional: true },
    ],
  },
  {
    key: "sale_business",
    label: "Sale of company",
    docs: [
      { key: "sale_agreement", label: "Sale agreement / SPA" },
      { key: "completion_statement", label: "Completion statement" },
      { key: "bank_statements", label: "Bank statement(s)", optional: true },
    ],
  },
  {
    key: "sale_real_estate",
    label: "Real estate sale",
    docs: [
      { key: "sale_agreement", label: "Sale agreement" },
      { key: "title_deed", label: "Title deed" },
      { key: "bank_statements", label: "Bank statement(s)", optional: true },
    ],
  },
  {
    key: "rental_income",
    label: "Rental income",
    docs: [
      { key: "rental_contract", label: "Rental contract" },
      { key: "title_deed", label: "Title deed" },
      { key: "bank_statements", label: "Bank statement(s)", optional: true },
    ],
  },
  {
    key: "investment_returns",
    label: "Investment returns",
    docs: [
      { key: "broker_statement", label: "Broker / portfolio statement" },
      { key: "trade_confirmation", label: "Trade confirmation(s)" },
      { key: "bank_statements", label: "Bank statement(s)", optional: true },
    ],
  },
  {
    key: "inheritance",
    label: "Inheritance",
    docs: [
      { key: "inheritance_certificate", label: "Certificate of inheritance" },
      { key: "grant_of_probate", label: "Grant of probate" },
      { key: "will_copy", label: "Copy of will" },
      { key: "bank_statements", label: "Bank statement(s)", optional: true },
    ],
  },
  {
    key: "gift",
    label: "Gift",
    docs: [
      { key: "gift_deed", label: "Gift deed" },
      { key: "donor_explanation", label: "Donor letter / explanation" },
      { key: "bank_statements", label: "Bank statement(s)", optional: true },
    ],
  },
  {
    key: "legal_settlement",
    label: "Legal settlement",
    docs: [
      { key: "court_order", label: "Court order / settlement agreement" },
      { key: "bank_statements", label: "Bank statement(s)", optional: true },
    ],
  },
  {
    key: "divorce_settlement",
    label: "Divorce settlement",
    docs: [
      { key: "divorce_decree", label: "Divorce decree / court order" },
      { key: "financial_settlement", label: "Financial settlement agreement" },
      { key: "bank_statements", label: "Bank statement(s)", optional: true },
    ],
  },
  {
    key: "virtual_asset_gains",
    label: "Virtual asset gains",
    docs: [
      { key: "exchange_statement", label: "Exchange statement" },
      { key: "trade_history", label: "Trade history / transaction report" },
      { key: "bank_statements", label: "Bank statement(s)", optional: true },
    ],
  },
  {
    key: "loan",
    label: "Loan",
    docs: [
      { key: "loan_agreement", label: "Loan agreement" },
      { key: "repayment_schedule", label: "Repayment schedule", optional: true },
      { key: "bank_statements", label: "Bank statement(s)", optional: true },
    ],
  },
  { key: "other", label: "Other", docs: [] },
];

export const SOW_LABEL_BY_VALUE = Object.fromEntries(
  SOW_DOCUMENT_SOURCES.map((source) => [source.key, source.label])
);

const SOW_VALUE_BY_LABEL = Object.fromEntries(
  SOW_DOCUMENT_SOURCES.map((source) => [source.label.toLowerCase(), source.key])
);

export function makeDocRef(doc) {
  if (typeof doc === "string") return { key: doc, label: "" };
  return {
    key: String(doc?.key || "").trim(),
    label: String(doc?.label || "").trim(),
    custom: doc?.custom === true,
  };
}

export function docKey(ref) {
  return makeDocRef(ref).key;
}

export function slugDocKey(value) {
  const base = String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 48);
  return base || `document_${Date.now()}`;
}

export function normalizeSowKey(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (SOW_LABEL_BY_VALUE[raw] || raw === "other") return raw;
  const lower = raw.toLowerCase().replace(/\s+/g, " ");
  return SOW_VALUE_BY_LABEL[lower] || raw;
}

export function sourceByKey(key) {
  return SOW_DOCUMENT_SOURCES.find((source) => source.key === key) || SOW_DOCUMENT_SOURCES[0];
}

export function labelForDoc(sourceKey, ref) {
  const normalized = makeDocRef(ref);
  if (!normalized.custom) {
    const source = sourceByKey(sourceKey);
    const fromSource = source.docs.find((doc) => doc.key === normalized.key)?.label;
    if (fromSource) return fromSource;
    for (const src of SOW_DOCUMENT_SOURCES) {
      const found = src.docs.find((doc) => doc.key === normalized.key)?.label;
      if (found) return found;
    }
  }
  return normalized.label || normalized.key;
}

function buildPolicyForModel(model, empty = false) {
  const cleanModel = normalizeRelationshipRiskModel(model);
  const riskOptions = supportingDocRisksForModel(cleanModel);
  const policy = {};

  SOW_DOCUMENT_SOURCES.forEach((source) => {
    const primaryDocs = source.docs.filter((doc) => !doc.optional);
    const optionalDocs = source.docs.filter((doc) => doc.optional);
    const group = primaryDocs.map((doc) => ({ key: doc.key, label: doc.label }));
    const defaultRule = empty
      ? { required: [], optional: [], groups: [] }
      : {
          required: [],
          optional: optionalDocs.map((doc) => ({ key: doc.key, label: doc.label })),
          groups: group.length
            ? [{ id: `${source.key}_evidence`, label: "Required group", minRequired: 1, documents: group }]
            : [],
        };

    policy[source.key] = {};
    riskOptions.forEach((risk) => {
      policy[source.key][risk.key] = clonePolicyConfig(defaultRule);
    });
  });

  return policy;
}

export function buildDefaultSupportingDocumentPolicy() {
  return {
    [RELATIONSHIP_RISK_MODEL_3_LEVEL]: buildPolicyForModel(RELATIONSHIP_RISK_MODEL_3_LEVEL),
    [RELATIONSHIP_RISK_MODEL_2_LEVEL]: buildPolicyForModel(RELATIONSHIP_RISK_MODEL_2_LEVEL),
  };
}

export function buildEmptySupportingDocumentPolicy() {
  return {
    [RELATIONSHIP_RISK_MODEL_3_LEVEL]: buildPolicyForModel(RELATIONSHIP_RISK_MODEL_3_LEVEL, true),
    [RELATIONSHIP_RISK_MODEL_2_LEVEL]: buildPolicyForModel(RELATIONSHIP_RISK_MODEL_2_LEVEL, true),
  };
}

export function clonePolicyConfig(cfg = {}) {
  return {
    required: (cfg.required || []).map(makeDocRef).filter((doc) => doc.key),
    optional: (cfg.optional || []).map(makeDocRef).filter((doc) => doc.key),
    groups: (cfg.groups || [])
      .map((group, idx) => ({
        id: String(group?.id || `group_${idx + 1}`),
        label: String(group?.label || "Required group"),
        minRequired: Number(group?.minRequired) || 1,
        documents: (group?.documents || []).map(makeDocRef).filter((doc) => doc.key),
      }))
      .filter((group) => group.documents.length),
  };
}

export function sanitizeSupportingDocumentPolicy(policy) {
  const fallback = buildDefaultSupportingDocumentPolicy();
  const out = {};

  [RELATIONSHIP_RISK_MODEL_3_LEVEL, RELATIONSHIP_RISK_MODEL_2_LEVEL].forEach((model) => {
    out[model] = {};
    const riskOptions = supportingDocRisksForModel(model);
    SOW_DOCUMENT_SOURCES.forEach((source) => {
      out[model][source.key] = {};
      riskOptions.forEach((risk) => {
        out[model][source.key][risk.key] = clonePolicyConfig(
          policy?.[model]?.[source.key]?.[risk.key] || fallback[model]?.[source.key]?.[risk.key]
        );
      });
    });
  });

  return out;
}

export function riskPolicy(policy, sourceKey, riskKey, model = RELATIONSHIP_RISK_MODEL_3_LEVEL) {
  const cleanModel = normalizeRelationshipRiskModel(model);
  const cleanRiskKey = normalizeRelationshipRiskLevel(riskKey);
  return (
    policy?.[cleanModel]?.[sourceKey]?.[cleanRiskKey] ||
    buildDefaultSupportingDocumentPolicy()?.[cleanModel]?.[sourceKey]?.[cleanRiskKey] ||
    { required: [], optional: [], groups: [] }
  );
}

export function effectiveGroupMin(group) {
  const count = (group?.documents || []).length;
  const max = count > 2 ? count - 1 : 1;
  return Math.min(Math.max(1, Number(group?.minRequired) || 1), max);
}

export function policySummary(policy, sourceKey, riskKey, model = RELATIONSHIP_RISK_MODEL_3_LEVEL) {
  const cfg = riskPolicy(policy, sourceKey, riskKey, model);
  const required = [...(cfg.required || [])];
  const groups = [];

  (cfg.groups || []).forEach((group) => {
    const documents = group.documents || [];
    if (documents.length === 1) {
      required.push(documents[0]);
      return;
    }
    if (documents.length > 1) groups.push(group);
  });

  return {
    required,
    optional: cfg.optional || [],
    groups,
  };
}

export function normalizeRelationshipRiskLevel(value) {
  return normalizeFinalRelationshipRiskLevel(value);
}

export function buildSowDocsMapFromPolicy(cfg = {}, existingDocs = {}) {
  const nextDocs = {};

  (cfg.required || []).forEach((doc) => {
    const key = docKey(doc);
    if (!key) return;
    const existing = existingDocs?.[key] || {};
    nextDocs[key] = {
      ...existing,
      label: labelForDoc("", doc),
      obtained: !!existing.obtained,
      requirement: "required",
    };
  });

  (cfg.groups || []).forEach((group) => {
    const groupDocs = group.documents || [];
    const minRequired = effectiveGroupMin(group);
    groupDocs.forEach((doc) => {
      const key = docKey(doc);
      if (!key) return;
      const existing = existingDocs?.[key] || {};
      const singleGroupDoc = groupDocs.length === 1;
      nextDocs[key] = {
        ...existing,
        label: labelForDoc("", doc),
        obtained: !!existing.obtained,
        requirement: singleGroupDoc ? "required" : "group",
        groupId: singleGroupDoc ? "" : group.id,
        groupLabel: singleGroupDoc ? "" : group.label || "Required group",
        minRequired: singleGroupDoc ? undefined : minRequired,
      };
    });
  });

  (cfg.optional || []).forEach((doc) => {
    const key = docKey(doc);
    if (!key) return;
    const existing = existingDocs?.[key] || {};
    nextDocs[key] = {
      ...existing,
      label: labelForDoc("", doc),
      obtained: !!existing.obtained,
      requirement: "optional",
    };
  });

  return nextDocs;
}

export function buildSowEntryFromPolicy({ sowKey, label, riskLevel, cfg, existing }) {
  const cleanCfg = clonePolicyConfig(cfg);
  return {
    ...(existing || {}),
    sowKey,
    label,
    riskLevel,
    otherDetails: existing?.otherDetails || "",
    required: cleanCfg.required,
    optional: cleanCfg.optional,
    groups: cleanCfg.groups,
    docs: buildSowDocsMapFromPolicy(cleanCfg, existing?.docs || {}),
  };
}

export function isSowEntrySatisfied(entry) {
  if (!entry) return false;
  if (entry.sowKey === "other") return String(entry.otherDetails || "").trim().length > 0;

  const docs = entry.docs || {};
  const directRequired = entry.required || [];
  const directOk = directRequired.every((doc) => {
    const key = docKey(doc);
    return key && !!docs?.[key]?.obtained;
  });
  if (!directOk) return false;

  return (entry.groups || []).every((group) => {
    const groupDocs = group.documents || [];
    if (groupDocs.length === 1) {
      const key = docKey(groupDocs[0]);
      return key && !!docs?.[key]?.obtained;
    }
    const obtained = groupDocs.filter((doc) => !!docs?.[docKey(doc)]?.obtained).length;
    return obtained >= effectiveGroupMin(group);
  });
}

export function buildSowMissingParts(entry) {
  if (!entry) return { required: [], groups: [], optional: [] };
  const docs = entry.docs || {};

  const required = (entry.required || [])
    .filter((doc) => !docs?.[docKey(doc)]?.obtained)
    .map((doc) => labelForDoc(entry.sowKey, doc));

  const groups = (entry.groups || [])
    .map((group) => {
      const groupDocs = group.documents || [];
      if (groupDocs.length === 1) {
        const only = groupDocs[0];
        return docs?.[docKey(only)]?.obtained
          ? null
          : { minRequired: 1, labels: [labelForDoc(entry.sowKey, only)], singleAsRequired: true };
      }
      const obtained = groupDocs.filter((doc) => !!docs?.[docKey(doc)]?.obtained).length;
      if (obtained >= effectiveGroupMin(group)) return null;
      return {
        minRequired: effectiveGroupMin(group),
        labels: groupDocs
          .filter((doc) => !docs?.[docKey(doc)]?.obtained)
          .map((doc) => labelForDoc(entry.sowKey, doc)),
      };
    })
    .filter(Boolean);

  const optional = (entry.optional || [])
    .filter((doc) => !docs?.[docKey(doc)]?.obtained)
    .map((doc) => labelForDoc(entry.sowKey, doc));

  return { required, groups, optional };
}

export function buildSowRequirementDescription(entry, { missingOnly = true } = {}) {
  if (!entry || entry.sowKey === "other") return "• Supporting document(s) required.";
  const docs = entry.docs || {};
  const lines = [];

  const requiredDocs = (entry.required || []).filter((doc) => !missingOnly || !docs?.[docKey(doc)]?.obtained);
  if (requiredDocs.length) {
    lines.push("Required:");
    requiredDocs.forEach((doc) => lines.push(`• ${labelForDoc(entry.sowKey, doc)}`));
  }

  (entry.groups || []).forEach((group) => {
    const groupDocs = group.documents || [];
    const relevantDocs = groupDocs.filter((doc) => !missingOnly || !docs?.[docKey(doc)]?.obtained);
    if (!relevantDocs.length) return;
    if (groupDocs.length === 1) {
      lines.push("Required:");
      relevantDocs.forEach((doc) => lines.push(`• ${labelForDoc(entry.sowKey, doc)}`));
      return;
    }
    lines.push(`Provide at least ${effectiveGroupMin(group)} of:`);
    relevantDocs.forEach((doc) => lines.push(`• ${labelForDoc(entry.sowKey, doc)}`));
  });

  const optionalDocs = (entry.optional || []).filter((doc) => !missingOnly || !docs?.[docKey(doc)]?.obtained);
  if (optionalDocs.length) {
    lines.push("Also provide (optional):");
    optionalDocs.forEach((doc) => lines.push(`• ${labelForDoc(entry.sowKey, doc)}`));
  }

  return lines.length ? lines.join("\n") : "";
}

export function buildSowRequirementOptions(entry, { missingOnly = true } = {}) {
  if (!entry) return [];
  const docs = entry.docs || {};
  const options = [];

  const push = (doc, type, groupId = "") => {
    const key = docKey(doc);
    if (!key || (missingOnly && docs?.[key]?.obtained)) return;
    const optionType = type === "group" ? "grp" : type === "optional" ? "opt" : "req";
    options.push({
      key: optionType === "grp" ? `sow:${entry.sowKey}:grp:${groupId}:${key}` : `sow:${entry.sowKey}:${optionType}:${key}`,
      label: labelForDoc(entry.sowKey, doc),
      requirement: type,
      groupId,
    });
  };

  (entry.required || []).forEach((doc) => push(doc, "required"));
  (entry.groups || []).forEach((group) => {
    const groupDocs = group.documents || [];
    groupDocs.forEach((doc) => push(doc, groupDocs.length === 1 ? "required" : "group", group.id));
  });
  (entry.optional || []).forEach((doc) => push(doc, "optional"));

  return options;
}
